"use client";

/**
 * The chat surface itself, shared by every route.
 *
 * Extracted so `/` and `/conversation/<id>` can render the SAME component
 * rather than two copies that drift. Neither route reads its params: the widget
 * reads the whole URL through the routing adapter, which also has to handle
 * `?panel=` and `#msg-` — splitting that knowledge between a route param and an
 * adapter would mean two sources of truth for one address.
 */

import { useMemo } from "react";
import { ChatWidget } from "@/src/core";
import type { ChatWidgetConfig } from "@/src/core";
import { createNextRouting } from "@/lib/next-routing";

// Build config from environment variables
const config: ChatWidgetConfig = {
  apiUrl: process.env.NEXT_PUBLIC_LLM_BASE_URL || "",
  apiKey: process.env.OPENAI_API_KEY,
  model: process.env.NEXT_PUBLIC_LLM_DEFAULT_MODEL,
  voiceApiUrl: process.env.NEXT_PUBLIC_VOICE_API_BASE_URL,
  voiceModel: process.env.NEXT_PUBLIC_VOICE_DEFAULT_MODEL,
  appName: process.env.NEXT_PUBLIC_APP_NAME || "AI Assist",
  enableVoice: true,
  enableFileUpload: true,
  enableCommands: true,
  enableTools: true,
  // MCP servers (from LM Studio's mcp.json) the LB attaches for server-side tool
  // execution. Sent as `integrations` to /api/v1/chat; without this the model is
  // never told tools exist. Matches chat2. (apps/llm AgentOrchestrator runs the loop.)
  mcpServers: ["mcp/mcp-proxy"],
  persistMessages: true,
  // Brain memory (tdd-0042): single per-app flag. Drives recall + capture + the
  // x-brain-recall:on header to the LB. Off unless NEXT_PUBLIC_ENABLE_MEMORY=true.
  // (Embedders of the widget set this via the config prop instead.)
  enableMemory: process.env.NEXT_PUBLIC_ENABLE_MEMORY === "true",
};

export function ChatApp() {
  // The routing adapter is what turns UI state into shareable URLs
  // (/conversation/<id>, ?panel=settings, #msg-<id>). Built once — a new
  // identity each render would resubscribe the widget's listener every time.
  //
  // Passed only here, in the app that owns the whole page. Embedders of
  // `chat/widget` (apps/benchmarks) and the web component pass no adapter and
  // keep their host page's URL to themselves.
  const routing = useMemo(() => createNextRouting(), []);
  const routedConfig = useMemo<ChatWidgetConfig>(
    () => ({ ...config, routing }),
    [routing],
  );

  // No height constraint. The document scrolls (see app/layout.tsx), so chat
  // grows with its content instead of fitting a computed viewport slice — which
  // is what previously left the message list confined to a fraction of the
  // screen when any link in the height chain resolved to `auto`.
  return <ChatWidget config={routedConfig} />;
}
